/* Reward rolls come from the app. A blank-origin product preview plays one fixed authored throw. */
if (!customElements.get('dope-dice-story')) {
  customElements.define('dope-dice-story', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.abort = new AbortController();
      this.interactive = this.querySelector('dope-dice-interactive');
      this.poster = this.querySelector('.dope-dice-story__poster');
      this.video = this.querySelector('[data-dice-film]');
      this.cameraHint = this.querySelector('[data-dice-camera-hint]');
      this.control = this.querySelector('[data-dice-control]');
      this.stage = this.querySelector('[data-dice-stage]') || this;
      this.managed = false;
      this.productPreview = this.dataset.productPreview === 'true' && !this.querySelector('dope-dice-rewards');
      this.previewPlayed = false;
      this.previewLoading = false;
      this.previewComplete = false;
      this.previewPausedByUser = false;
      this.previewState = 'idle';
      this.hasOutcome = false;
      this.inView = false;
      try {
        this.sources = JSON.parse(this.querySelector('[data-dice-sources]')?.textContent || '[]');
        this.posters = JSON.parse(this.querySelector('[data-dice-posters]')?.textContent || '[]');
        this.videos = JSON.parse(this.querySelector('[data-dice-videos]')?.textContent || '[]');
      } catch { this.sources = []; this.posters = []; this.videos = []; }
      if (!Array.isArray(this.sources) || !Array.isArray(this.posters)) { this.sources = []; this.posters = []; }
      if (!Array.isArray(this.videos)) this.videos = [];
      this.preference = matchMedia('(prefers-reduced-motion: reduce)');
      this.connection = navigator.connection;
      const options = {signal:this.abort.signal};
      this.addEventListener('dope:dice-rewards-enabled', () => { this.managed = true; this.updatePreviewControl(); }, options);
      this.control?.addEventListener('click', () => {
        if (!this.productPreview || this.managed || this.previewComplete) return;
        if (this.previewState === 'playing') {
          this.previewPausedByUser = true;
          this.previewPlayback?.pause();
        } else {
          this.previewPausedByUser = false;
          if (this.previewPlayback) this.previewPlayback.resume({manual:true});
          else this.playProductPreview({manual:true});
        }
      }, options);
      this.updatePreviewControl();
      this.addEventListener('dope:dice-context-lost', () => {
        if (this.productPreview && !this.managed && this.interactive) this.interactive.unavailable = true;
        this.classList.remove('has-interactive-frame');
        this.updateCameraHint(false);
        if (this.fallbackOutcome) this.fallbackOutcome();
        else this.stop({reset:true});
      }, options);
      this.preference.addEventListener('change', () => { if (this.preference.matches) this.stop(); else this.preview(); }, options);
      this.connection?.addEventListener?.('change', () => { if (this.connection.saveData) this.stop(); else this.preview(); }, options);
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.stop(); else this.preview(); }, options);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          const entry = entries.find(item => item.target === this.stage);
          if (!entry) return;
          this.inView = entry.isIntersecting && (entry.intersectionRatio >= .35 || (this.previewPlayback && entry.intersectionRatio > 0));
          if (!this.inView) this.stop();
          else this.preview();
        }, {threshold:[0,.35]});
        this.observer.observe(this.stage);
      } else { this.inView = true; this.preview(); }
    }
    revealStage() {
      if (!this.stage.scrollIntoView || !this.stage.getBoundingClientRect) return;
      this.stage.scrollIntoView({block:'center',behavior:'instant'});
      const rect=this.stage.getBoundingClientRect(),height=window.innerHeight||document.documentElement?.clientHeight;
      this.inView=rect.height>0&&(Math.min(rect.bottom,height)-Math.max(rect.top,0))/rect.height>=.35;
    }
    updatePreviewControl() {
      if (!this.control) return;
      this.control.hidden = !this.productPreview || this.managed || this.previewComplete;
      this.control.disabled = this.previewState === 'loading';
      this.control.setAttribute('aria-busy',String(this.previewState === 'loading'));
      this.control.textContent = this.previewState === 'loading' ? this.control.dataset.loading : this.previewState === 'playing' ? this.control.dataset.pause : this.previewState === 'paused' ? this.control.dataset.resume : this.control.dataset.play;
    }
    updateCameraHint(visible) { if (this.cameraHint) this.cameraHint.hidden = !visible; }
    prepareOutcome({signal} = {}) {
      if (signal?.aborted || this.abort.signal.aborted) return Promise.reject(new DOMException('Disconnected','AbortError'));
      if (!this.interactive || this.interactive.unavailable || this.preference.matches || this.connection?.saveData || document.hidden) return Promise.resolve('video');
      return new Promise((resolve,reject) => {
        const local = new AbortController();let settled = false;
        const finish = (mode,error) => {
          if (settled) return;
          settled = true;clearTimeout(timeout);local.abort();
          if (error) reject(error);else resolve(mode);
        };
        const timeout = setTimeout(() => {
          this.interactive.unavailable = true;this.interactive.pause();
          this.updateCameraHint(false);finish('video');
        },3000);
        const abort = () => finish(null,new DOMException('Disconnected','AbortError'));
        signal?.addEventListener('abort',abort,{once:true,signal:local.signal});
        this.abort.signal.addEventListener('abort',abort,{once:true,signal:local.signal});
        this.interactive.ready().then(() => finish('interactive')).catch(() => finish('video'));
      });
    }
    async preview() {
      if (!this.inView || document.hidden || this.preference.matches || this.connection?.saveData) return;
      if (this.productPreview) return this.playProductPreview();
      if (this.hasOutcome) return;
      try {
        const shown = await this.interactive?.preview?.();
        if (shown && !this.hasOutcome && this.inView && !document.hidden && !this.preference.matches && !this.connection?.saveData && !this.abort.signal.aborted) {
          this.classList.add('has-frame','has-interactive-frame');
          this.updateCameraHint(true);
        }
      } catch { /* The poster remains a complete visual fallback. */ }
    }
    async playProductPreview({manual=false} = {}) {
      if (!this.productPreview || this.managed || this.previewComplete || this.previewPausedByUser) return;
      if(manual&&!this.inView)this.revealStage();
      if (this.previewPlayback) { this.previewPlayback.resume({manual}); return; }
      if (this.previewLoading) return;
      this.previewLoading = true;
      this.previewState = 'loading';this.updatePreviewControl();
      try {
        await this.prepareOutcome({signal:this.abort.signal});
        if (this.managed || !this.inView || document.hidden || (!manual && (this.preference.matches || this.connection?.saveData)) || this.abort.signal.aborted) return;
        this.previewPlayed = true;
        await this.playVisual('01',{explicitMotion:manual});
      } catch { /* A neutral product poster remains available. */ }
      finally { this.previewLoading = false; if (this.previewState === 'loading') this.previewState = 'idle'; this.updatePreviewControl(); }
    }
    showOutcome(filmId) {
      if(!this.managed||!this.isConnected||this.abort.signal.aborted)return;
      this.showVisual(filmId);
    }
    showVisual(filmId) {
      if(!this.isConnected||this.abort.signal.aborted)return;
      const index=Number(filmId)-1;
      if(!Number.isInteger(index)||index<0||index>=this.sources.length)return;
      this.hasOutcome = true;
      if(this.poster&&this.posters[index])this.poster.src=this.posters[index];
      this.video?.pause();
      this.interactive?.pause();
      const model=this.interactive?.showResult(index);
      this.classList.remove('is-playing','has-video-frame');
      if(model)this.classList.add('has-frame','has-interactive-frame');
      else this.classList.remove('has-frame','has-interactive-frame');
      this.updateCameraHint(!!model);
    }
    playOutcome(filmId, {signal} = {}) {
      if(!this.managed||!this.isConnected||this.abort.signal.aborted||signal?.aborted)return Promise.resolve();
      return this.playVisual(filmId,{signal});
    }
    playVisual(filmId, {signal,explicitMotion=false} = {}) {
      if(!this.isConnected||this.abort.signal.aborted||signal?.aborted)return Promise.resolve();
      const index=Number(filmId)-1;
      if(!Number.isInteger(index)||index<0||index>=this.sources.length)return Promise.resolve();
      this.hasOutcome = true;
      if(this.poster&&this.posters[index])this.poster.src=this.posters[index];
      this.stop({reset:true});
      this.updateCameraHint(false);
      const showResult=()=>this.showVisual(filmId);
      const product = this.productPreview && !this.managed;
      const motionAllowed = () => (product && explicitMotion) || (!this.preference.matches && !this.connection?.saveData);
      if(!this.inView&&motionAllowed()&&!document.hidden&&this.stage.scrollIntoView&&this.stage.getBoundingClientRect){
        this.revealStage();
      }
      if(!motionAllowed()||document.hidden||!this.inView){showResult();return Promise.resolve();}
      return new Promise(resolve=>{
        const local=new AbortController();let settled=false,videoStarted=false,interactiveStarted=false,loadTimeout,timeout,paused=false,videoAttempt=0;
        const finish=(complete=false)=>{
          if(settled)return;
          settled=true;++videoAttempt;clearTimeout(timeout);clearTimeout(loadTimeout);local.abort();
          this.previewPlayback=null;this.fallbackOutcome=null;showResult();this.finishOutcome=null;
          if(product){this.previewComplete=complete;this.previewState=complete?'done':'idle';this.updatePreviewControl();}
          resolve();
        };
        const armTimeout=()=>{clearTimeout(timeout);timeout=setTimeout(()=>finish(),18000);};
        const pause=()=>{
          if(settled)return;
          paused=true;++videoAttempt;clearTimeout(timeout);clearTimeout(loadTimeout);this.interactive?.pause();this.video?.pause();
          this.classList.remove('is-playing');this.previewState='paused';this.updatePreviewControl();
        };
        const playVideo=()=>{
          if(settled||document.hidden||!this.inView){if(product)pause();else finish();return;}
          paused=false;armTimeout();
          if(product){this.previewState='playing';this.updatePreviewControl();}
          const attempt=++videoAttempt;
          Promise.resolve(this.video.play()).catch(()=>{if(settled||attempt!==videoAttempt)return;if(product)pause();else finish();});
        };
        const resume=({manual=false}={})=>{
          if(product&&manual&&!this.inView)this.revealStage();
          if(settled||!paused||document.hidden||!this.inView)return;
          if(product&&manual)explicitMotion=true;
          paused=false;armTimeout();this.previewState='playing';this.updatePreviewControl();
          if(videoStarted)playVideo();
          else startInteractive(false);
        };
        const fallback=()=>{
          if(settled||videoStarted)return;
          if(product&&paused)return;
          clearTimeout(loadTimeout);this.interactive?.pause();
          this.classList.remove('has-interactive-frame');this.updateCameraHint(false);
          const source=this.videos[index];
          if(!this.video||typeof source!=='string'||!source||signal?.aborted){finish();return;}
          if(document.hidden||!this.inView||!motionAllowed()){if(product)pause();else finish();return;}
          videoStarted=true;
          this.video.addEventListener('playing',()=>{
            if(!settled&&!paused)this.classList.add('has-frame','has-video-frame','is-playing');
          },{signal:local.signal});
          this.video.addEventListener('ended',()=>finish(true),{once:true,signal:local.signal});
          this.video.addEventListener('error',()=>finish(),{once:true,signal:local.signal});
          this.video.src=source;this.video.currentTime=0;this.video.load();
          playVideo();
        };
        const startInteractive=restart=>{
          if(!this.interactive||this.interactive.unavailable){fallback();return;}
          loadTimeout=setTimeout(fallback,3000);
          this.interactive.play(index,{restart:restart||!interactiveStarted,onFinish:()=>finish(true)}).then(played=>{
            if(played)interactiveStarted=true;
            if(settled||videoStarted||paused)return;
            clearTimeout(loadTimeout);
            if(!played){fallback();return;}
            this.classList.add('has-frame','has-interactive-frame','is-playing');
          }).catch(fallback);
        };
        armTimeout();this.finishOutcome=()=>finish();this.fallbackOutcome=fallback;
        if(product){this.previewPlayback={pause,resume};this.previewState='playing';this.updatePreviewControl();}
        signal?.addEventListener('abort',()=>finish(),{once:true,signal:local.signal});
        this.abort.signal.addEventListener('abort',()=>finish(),{once:true,signal:local.signal});
        startInteractive(true);
      });
    }
    stop({reset=false}={}) {
      if(!reset&&this.productPreview&&!this.managed&&this.previewPlayback){this.previewPlayback.pause();return;}
      this.finishOutcome?.();
      this.interactive?.pause();
      this.video?.pause();
      this.classList.remove('is-playing','has-video-frame');
      if (reset) this.classList.remove('has-frame','has-interactive-frame');
      if (reset) this.updateCameraHint(false);
    }
    disconnectedCallback() {
      this.stop({reset:true});
      this.managed = false;
      this.observer?.disconnect();
      this.abort?.abort();
    }
  });
}
